import React, { useState, useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { convertirAMonedaLocal, getDolarValue } from "../../../utils/calculateVentas";
import { MESES } from "../constants";

// Colores por tipo
const COLORS = {
  cierres: { bar: "#0b4b1a", line: "#28a745", label: "Cierres" },
  facturacion: { bar: "#0c0c58", line: "#3498db", label: "Facturación" },
};

const formatCompact = (value, pais, showInDollars = false) => {
  if (showInDollars) {
    // USD: K (miles) — los rangos típicos no llegan a M
    if (Math.abs(value) >= 1_000) return `${Math.round(value / 1_000)}K`;
    return Math.round(value).toString();
  }
  if (pais === "Peru") {
    // Perú: soles en K (miles)
    if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
    return Math.round(value).toString();
  }
  // Chile: pesos en M (millones)
  if (value >= 1_000_000) return `${Math.round(value / 1_000_000)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return Math.round(value).toString();
};

const CustomTooltip = ({ active, payload, label, getMoneda, getDisplayValue, tooltipLabels }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-title">{label}</p>
      {payload.map((entry) => {
        const meta = tooltipLabels[entry.dataKey];
        if (!meta || !entry.value) return null;
        return (
          <p key={entry.dataKey} className="chart-tooltip-row" style={{ color: meta.color }}>
            <span className="chart-tooltip-dot" style={{ background: meta.color }} />
            {meta.label}: {getMoneda()}
            {getDisplayValue(entry.value)}
          </p>
        );
      })}
    </div>
  );
};

export const ChartModal = ({
  open,
  onClose,
  getDesglose,
  getMoneda,
  getDisplayValue,
  currentMonthIndex,
  pais,
  styles,
  oportunidades,
  contratosData,
  showInDollars,
}) => {
  const [activeTab, setActiveTab] = useState("proyeccion");
  const [acumViewCierres, setAcumViewCierres] = useState(true);
  const [acumViewFact, setAcumViewFact] = useState(true);
  const [acumVertical, setAcumVertical] = useState("publicidad");
  // "cierres" = barras son cierres, líneas son facturación
  // "facturacion" = barras son facturación, líneas son cierres
  const [primaryView, setPrimaryView] = useState("cierres");
  const [showSecondary, setShowSecondary] = useState(true);

  const chartData = useMemo(() => {
    if (!getDesglose) return [];

    return MESES.map((mes, i) => {
      const c26 = getDesglose(2026, i + 1, i + 1, "cierres") || {};
      const f26 = getDesglose(2026, i + 1, i + 1, "facturacion") || {};
      const c25 = getDesglose(2025, i + 1, i + 1, "cierres") || {};
      const f25 = getDesglose(2025, i + 1, i + 1, "facturacion") || {};

      const isFutureMonth = i > currentMonthIndex;

      return {
        mes,
        cierres2026: isFutureMonth ? 0 : c26.total || 0,
        facturacion2026: f26.total || 0,
        cierres2025: c25.total || 0,
        facturacion2025: f25.total || 0,
      };
    });
  }, [getDesglose, currentMonthIndex]);

  const isCierresPrimary = primaryView === "cierres";
  const primary = isCierresPrimary ? "cierres" : "facturacion";
  const secondary = isCierresPrimary ? "facturacion" : "cierres";

  const barKey2026 = `${primary}2026`;
  const barKey2025 = `${primary}2025`;
  const lineKey2026 = `${secondary}2026`;
  const lineKey2025 = `${secondary}2025`;

  const tooltipLabels = {
    [barKey2026]: { label: `${COLORS[primary].label} 2026`, color: COLORS[primary].bar },
    [barKey2025]: { label: `${COLORS[primary].label} 2025`, color: COLORS[primary].line },
    [lineKey2026]: { label: `${COLORS[secondary].label} 2026`, color: COLORS[secondary].bar },
    [lineKey2025]: { label: `${COLORS[secondary].label} 2025`, color: COLORS[secondary].line },
  };

  // Promedios para modo solitario
  const avg2025 = useMemo(() => {
    if (chartData.length === 0) return 0;
    const vals = chartData.map((d) => d[barKey2025]).filter(Boolean);
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / 12 : 0;
  }, [chartData, barKey2025]);

  const avg2026 = useMemo(() => {
    if (chartData.length === 0) return 0;
    const vals = chartData.map((d) => d[barKey2026]).filter(Boolean);
    // Para cierres 2026, promediar solo sobre meses transcurridos
    const divisor = primary === "cierres" ? currentMonthIndex + 1 : 12;
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / divisor : 0;
  }, [chartData, barKey2026, primary, currentMonthIndex]);

  const acumulacionData = useMemo(() => {
    if (activeTab !== "acumulacion")
      return { diasHabiles: [], cierresFull: [], facturacionFull: [], runRateCierres: 0, runRateFact: 0 };

    // Generar días hábiles 2026 (lunes a viernes)
    const dias = [];
    const d = new Date(2026, 0, 1);
    const fin = new Date(2026, 11, 31);
    while (d <= fin) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) dias.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }

    // Helper: convierte un monto al modo de visualización elegido
    const toDisplay = (importe, monedaDolar, fechaKey) => {
      const amount = importe || 0;
      if (!amount) return 0;
      if (showInDollars) {
        if (monedaDolar) return amount; // ya viene en USD
        const tc = getDolarValue(fechaKey, pais) || 1;
        return amount / tc;
      }
      return monedaDolar ? convertirAMonedaLocal(amount, "USD", pais, fechaKey) : amount;
    };

    // Indexar cierres por fecha exacta (solo estatus 100% con fecha_cierre)
    const cierresPorFecha = {};
    (oportunidades || []).forEach((o) => {
      if (o.estatus !== "100%" || !o.fecha_cierre) return;
      const key = o.fecha_cierre.slice(0, 10);
      cierresPorFecha[key] = (cierresPorFecha[key] || 0) + toDisplay(o.importe, o.moneda_dolar, key);
    });

    // Cierres de sales_contratos (importe_total por fecha_cierre)
    (contratosData || []).forEach((c) => {
      if (c.estatus !== "100%" || !c.fecha_cierre) return;
      const key = c.fecha_cierre.slice(0, 10);
      cierresPorFecha[key] = (cierresPorFecha[key] || 0) + toDisplay(c.importe_total, c.moneda_dolar, key);
    });

    // Indexar facturación por fecha exacta (facturafecha)
    const factPorFecha = {};
    (oportunidades || []).forEach((o) => {
      if (!o.facturafecha) return;
      const key = o.facturafecha.slice(0, 10);
      factPorFecha[key] = (factPorFecha[key] || 0) + toDisplay(o.importe, o.moneda_dolar, key);
    });

    // Facturación de sales_contratos (meses jsonb)
    (contratosData || []).forEach((c) => {
      if (!c.meses) return;
      c.meses.forEach((m) => {
        if (!m.facturafecha) return;
        const key = m.facturafecha.slice(0, 10);
        factPorFecha[key] = (factPorFecha[key] || 0) + toDisplay(m.importe, c.moneda_dolar, key);
      });
    });

    // Construir acumulado día a día
    const hoy = new Date();
    let acumCierres = 0;
    let acumFact = 0;
    const cierres = [];
    const facturacion = [];

    dias.forEach((dia) => {
      const key = dia.toISOString().slice(0, 10);
      const esFuturo = dia > hoy;
      acumCierres += cierresPorFecha[key] || 0;
      acumFact += factPorFecha[key] || 0;
      cierres.push(esFuturo ? null : acumCierres);
      facturacion.push(esFuturo ? null : acumFact);
    });

    // Propagar último valor conocido hasta fin de año
    const cierresFull = cierres.map((v, i) => {
      if (v !== null) return v;
      const anterior = cierres
        .slice(0, i)
        .reverse()
        .find((x) => x !== null);
      return anterior ?? null;
    });
    const facturacionFull = facturacion.map((v, i) => {
      if (v !== null) return v;
      const anterior = facturacion
        .slice(0, i)
        .reverse()
        .find((x) => x !== null);
      return anterior ?? null;
    });

    // Run rate: YTD / meses transcurridos × 12
    const hoyMes = hoy.getMonth(); // 0-indexed
    const mesesTranscurridos = hoyMes + 1;
    const ytdCierres = cierres.filter((v) => v !== null).at(-1) ?? 0;
    const ytdFact = facturacion.filter((v) => v !== null).at(-1) ?? 0;
    const runRateCierres = mesesTranscurridos > 0 ? Math.round((ytdCierres / mesesTranscurridos) * 12) : 0;
    const runRateFact = mesesTranscurridos > 0 ? Math.round((ytdFact / mesesTranscurridos) * 12) : 0;

    return { diasHabiles: dias, cierresFull, facturacionFull, runRateCierres, runRateFact };
  }, [activeTab, oportunidades, contratosData, pais, showInDollars]);

  const acumYMax = useMemo(() => {
    const maxC = acumulacionData.runRateCierres || 0;
    const maxF = acumulacionData.runRateFact || 0;
    const maxReal = Math.max(
      ...acumulacionData.cierresFull.filter((v) => v !== null),
      ...acumulacionData.facturacionFull.filter((v) => v !== null),
      0
    );
    return Math.ceil(Math.max(maxC, maxF, maxReal) * 1.05);
  }, [acumulacionData]);

  // No permitir deseleccionar si es el único activo
  const toggleCierres = () => {
    if (acumViewCierres && !acumViewFact) return;
    setAcumViewCierres((v) => !v);
  };
  const toggleFact = () => {
    if (acumViewFact && !acumViewCierres) return;
    setAcumViewFact((v) => !v);
  };

  if (!open) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "85vw",
          maxWidth: 1350,
          paddingTop: 20,
          minHeight: "70vh",
          height: "88vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 20px 16px",
            flexShrink: 0,
          }}
        >
          {/* Izquierda: tabs */}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className={`chart-pill${activeTab === "proyeccion" ? " chart-pill--active" : ""}`}
              onClick={() => setActiveTab("proyeccion")}
            >
              Proyección comercial
            </button>
            <button
              className={`chart-pill${activeTab === "acumulacion" ? " chart-pill--active" : ""}`}
              onClick={() => setActiveTab("acumulacion")}
            >
              Acumulación comercial
            </button>
          </div>

          {/* Centro: badge US$ */}
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            {showInDollars && (
              <span
                style={{
                  fontSize: 11,
                  color: "#1a4a8a",
                  fontWeight: 500,
                  background: "#e8eef5",
                  padding: "2px 8px",
                  borderRadius: 4,
                }}
              >
                US$
              </span>
            )}
          </div>

          {/* Derecha: verticales + X */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {activeTab === "acumulacion" && (
              <>
                <button
                  className={`chart-pill${acumVertical === "publicidad" ? " chart-pill--active-green" : ""}`}
                  onClick={() => setAcumVertical("publicidad")}
                >
                  Publicidad
                </button>
                <button className="chart-pill" disabled style={{ opacity: 0.35, cursor: "not-allowed" }}>
                  Hardware
                </button>
                <button className="chart-pill" disabled style={{ opacity: 0.35, cursor: "not-allowed" }}>
                  Gestión & Contenidos
                </button>
                <button className="chart-pill" disabled style={{ opacity: 0.35, cursor: "not-allowed" }}>
                  BTL
                </button>
              </>
            )}
            <button
              onClick={onClose}
              aria-label="Cerrar"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "0.5px solid var(--color-border-secondary)",
                background: "var(--color-background-secondary)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                color: "var(--color-text-secondary)",
                flexShrink: 0,
                marginLeft: 8,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {activeTab === "proyeccion" && (
          <div className={styles.modalBody} style={{ flex: 1, overflow: "auto", paddingTop: 12 }}>
            {/* Gráfico */}
            <div style={{ width: "100%", height: "60vh", minHeight: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 20, right: 80, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis
                    dataKey="mes"
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#ddd" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#999" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCompact(v, pais)}
                    width={55}
                  />
                  <Tooltip
                    content={
                      <CustomTooltip
                        getMoneda={getMoneda}
                        getDisplayValue={getDisplayValue}
                        tooltipLabels={tooltipLabels}
                      />
                    }
                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  />

                  {/* Línea del mes actual */}
                  <ReferenceLine
                    x={MESES[currentMonthIndex]}
                    stroke="#c57b1a"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    label={{
                      value: "Hoy",
                      position: "insideTopRight",
                      fill: "#c57b1a",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  />

                  {/* Barras: concepto principal (2025 izquierda, 2026 derecha) */}
                  <Bar
                    dataKey={barKey2025}
                    fill={COLORS[primary].line}
                    radius={[3, 3, 0, 0]}
                    barSize={16}
                    opacity={0.45}
                  />
                  <Bar
                    dataKey={barKey2026}
                    fill={COLORS[primary].bar}
                    radius={[3, 3, 0, 0]}
                    barSize={20}
                    opacity={0.85}
                  />

                  {/* Líneas: concepto secundario (2026 sólida, 2025 punteada) */}
                  {showSecondary && (
                    <Line
                      dataKey={lineKey2026}
                      stroke={COLORS[secondary].bar}
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: COLORS[secondary].bar }}
                      activeDot={{ r: 5 }}
                      type="monotone"
                    />
                  )}
                  {showSecondary && (
                    <Line
                      dataKey={lineKey2025}
                      stroke={COLORS[secondary].line}
                      strokeWidth={2}
                      strokeDasharray="6 3"
                      dot={{ r: 3, fill: COLORS[secondary].line }}
                      activeDot={{ r: 5 }}
                      type="monotone"
                    />
                  )}

                  {/* Promedios horizontales: solo en modo solitario */}
                  {!showSecondary && avg2025 > 0 && (
                    <ReferenceLine
                      y={avg2025}
                      stroke={COLORS[primary].line}
                      strokeDasharray="8 4"
                      strokeWidth={1}
                      label={{
                        value: `2025: ${formatCompact(avg2025, pais)}`,
                        position: "right",
                        fill: COLORS[primary].line,
                        fontSize: 10,
                      }}
                    />
                  )}
                  {!showSecondary && avg2026 > 0 && (
                    <ReferenceLine
                      y={avg2026}
                      stroke={COLORS[primary].bar}
                      strokeDasharray="8 4"
                      strokeWidth={1}
                      label={{
                        value: `2026: ${formatCompact(avg2026, pais)}`,
                        position: "right",
                        fill: COLORS[primary].bar,
                        fontSize: 10,
                      }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Leyenda dinámica (2025 primero, 2026 después) */}
            <div className="chart-legend">
              <div className="chart-legend-item">
                <span className="chart-legend-bar" style={{ background: COLORS[primary].line, opacity: 0.45 }} />
                {COLORS[primary].label} 2025
              </div>
              <div className="chart-legend-item">
                <span className="chart-legend-bar" style={{ background: COLORS[primary].bar }} />
                {COLORS[primary].label} 2026
              </div>
              {showSecondary && (
                <>
                  <div className="chart-legend-item">
                    <span className="chart-legend-line" style={{ borderColor: COLORS[secondary].line }} />
                    {COLORS[secondary].label} 2025
                  </div>
                  <div className="chart-legend-item">
                    <span
                      className="chart-legend-line"
                      style={{ borderColor: COLORS[secondary].bar, borderStyle: "solid" }}
                    />
                    {COLORS[secondary].label} 2026
                  </div>
                </>
              )}
            </div>

            {/* Pills de tipo - toggle exclusivo */}
            <div className="chart-controls" style={{ justifyContent: "center", marginTop: 12, gap: 8 }}>
              <button
                className={`chart-pill ${isCierresPrimary ? "active" : ""}`}
                style={
                  isCierresPrimary
                    ? { background: COLORS.cierres.bar, borderColor: COLORS.cierres.bar, color: "#fff" }
                    : {}
                }
                onClick={() => setPrimaryView("cierres")}
              >
                Cierres
              </button>
              <button
                className={`chart-pill ${!isCierresPrimary ? "active" : ""}`}
                style={
                  !isCierresPrimary
                    ? { background: COLORS.facturacion.bar, borderColor: COLORS.facturacion.bar, color: "#fff" }
                    : {}
                }
                onClick={() => setPrimaryView("facturacion")}
              >
                Facturación
              </button>
            </div>
            {/* Checkbox comparación debajo */}
            <div style={{ textAlign: "center", marginTop: 6 }}>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: "0.85rem",
                  color: "#666",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={showSecondary}
                  onChange={() => setShowSecondary((p) => !p)}
                  style={{ cursor: "pointer" }}
                />
                Sumar {COLORS[secondary].label}
              </label>
            </div>
          </div>
        )}

        {activeTab === "acumulacion" && (
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              padding: "8px 0",
              paddingTop: 12,
            }}
          >
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={acumulacionData.diasHabiles.map((d, i) => ({
                    dia: d.toISOString().slice(0, 10),
                    label:
                      d.getDate() === 1
                        ? ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"][
                            d.getMonth()
                          ]
                        : "",
                    cierres: acumulacionData.cierresFull[i],
                    facturacion: acumulacionData.facturacionFull[i],
                  }))}
                  margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    ticks={["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"]}
                    interval={0}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(v) => formatCompact(v, pais, showInDollars)}
                    width={55}
                    tick={{ fontSize: 11 }}
                    domain={[0, acumYMax]}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const dia = payload[0]?.payload?.dia;
                      return (
                        <div className="chart-tooltip">
                          <div className="chart-tooltip-title">{dia}</div>
                          {payload.map(
                            (p, i) =>
                              p.value != null && (
                                <div key={i} className="chart-tooltip-row">
                                  <span className="chart-tooltip-dot" style={{ background: p.color }} />
                                  {p.name}: {getDisplayValue(p.value)}
                                </div>
                              )
                          )}
                        </div>
                      );
                    }}
                  />
                  {acumViewCierres && (
                    <Area
                      type="monotone"
                      dataKey="cierres"
                      name="Cierres"
                      stroke="#1a7f4b"
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                      fill="#1a7f4b"
                      fillOpacity={0.15}
                    />
                  )}
                  {acumViewFact && (
                    <Area
                      type="monotone"
                      dataKey="facturacion"
                      name="Facturación"
                      stroke="#1a4a8a"
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                      fill="#1a4a8a"
                      fillOpacity={0.12}
                    />
                  )}
                  {acumViewCierres && acumulacionData.runRateCierres > 0 && (
                    <ReferenceLine
                      y={acumulacionData.runRateCierres}
                      stroke="#1a7f4b"
                      strokeDasharray="6 3"
                      strokeWidth={1}
                      label={{
                        value: "RR Cierres",
                        position: "insideBottomRight",
                        fontSize: 10,
                        fill: "#1a7f4b",
                      }}
                    />
                  )}
                  {acumViewFact && acumulacionData.runRateFact > 0 && (
                    <ReferenceLine
                      y={acumulacionData.runRateFact}
                      stroke="#1a4a8a"
                      strokeDasharray="6 3"
                      strokeWidth={1}
                      label={{
                        value: "RR Fact",
                        position: "insideBottomRight",
                        fontSize: 10,
                        fill: "#1a4a8a",
                      }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-legend" style={{ marginTop: 8, justifyContent: "center" }}>
              {acumViewCierres && (
                <div className="chart-legend-item">
                  <span className="chart-legend-line" style={{ background: "#1a7f4b" }} />
                  Cierres acumulados 2026
                </div>
              )}
              {acumViewFact && (
                <div className="chart-legend-item">
                  <span className="chart-legend-line" style={{ background: "#1a4a8a" }} />
                  Facturación acumulada 2026
                </div>
              )}
            </div>

            <div className="chart-controls" style={{ justifyContent: "center", marginTop: 12 }}>
              <button
                className={`chart-pill${acumViewCierres ? " chart-pill--active-green" : ""}`}
                onClick={toggleCierres}
              >
                Cierres
              </button>
              <button className={`chart-pill${acumViewFact ? " chart-pill--active-blue" : ""}`} onClick={toggleFact}>
                Facturación
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
